import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useDataContext } from '../contexts/DataContext';
import { JobActionsBar } from '../components/Jobs/JobActionsBar';
import JobAccordion from "../components/Jobs/JobAccordian";
import JobHeader from "../components/Jobs/JobHeader";
import { JobDetails } from "../components/Jobs/JobDetails";
import { Button, CircularProgress, Container, ButtonGroup, Grid, Box } from "@mui/material";
import { KeyboardBackspace } from '@mui/icons-material';
import { req, parseJsonProperties, log } from '../utils';
import { DisplayCompletedJob } from "../components/Jobs/DisplayCompletedJob";
import { InstallMachine } from "../components/Jobs/moving_machines/InstallMachine";
import { RemoveMachine } from "../components/Jobs/moving_machines/RemoveMachine";
import { SwapMachine } from "../components/Jobs/moving_machines/SwapMachine";
import { normalizeJobRecord } from "../utils/jobMachines";
// Memoizing JobDetails, JobAccordion, and MoveMachine components to prevent unnecessary re-renders
const JobDetailsMemoized = React.memo(JobDetails);
const JobAccordionMemoized = React.memo(JobAccordion);

const JobHeaderMemoized = React.memo(JobHeader);
export function Job() {
  const { loading, updateProperty, jobs, setJobs, machines, refreshMachines } = useDataContext();
  const { service_call_id } = useParams();
  const [pageError, setPageError] = useState(false);
  const [pageErrorMessage, setPageErrorMessage] = useState('');
  const [jobMissing, setJobMissing] = useState(false);
  const machineRefreshAttemptedForCall = useRef(null);

  useEffect(() => {
    machineRefreshAttemptedForCall.current = null;
  }, [service_call_id]);

  // Finding the job in the jobs array or defaulting to false
  const job = useMemo(() => jobs.find(item => String(item.service_call_id) === String(service_call_id)) || false, [jobs, service_call_id]);

  // Computing the defaultMachine based on the job details
  const defaultMachine = useMemo(() => job?.machine_ids_array?.length === 1 ? job.machine_ids_array[0] : null, [job]);

  // Fetch job details if not available in the context
  useEffect(() => {
    if (!job) {
      setJobMissing(true);
      try {
        req('get', 'get_job_with_images', { service_call_id })
          .then(data => {
            if (data) {
              setJobs((prev) => [
                ...prev,
                normalizeJobRecord(parseJsonProperties(data)),
              ]);
              setJobMissing(false);
            } else {
              setJobMissing(false);
              setPageError(true);
              setPageErrorMessage('Job not found');
            }
          })
          .catch(error => {
            log("Error fetching job data", error);
            setJobMissing(false);
            setPageError(true);
            setPageErrorMessage('An error occurred while fetching job details');
          });
      } catch (error) {
        log("Error fetching job data", error);
        setJobMissing(false);
        setPageError(true);
        setPageErrorMessage('An error occurred while fetching job details');
      }
    } else {
      setJobMissing(false);
      setPageError(false);
      setPageErrorMessage('');
    }
  }, [service_call_id, job, setJobs]);

  useEffect(() => {
    if (!job || !Array.isArray(job.machine_ids_array) || job.machine_ids_array.length === 0) {
      return;
    }
    const missing = job.machine_ids_array.some(
      (id) => !machines.find((m) => String(m.machine_id_pk) === String(id))
    );
    if (!missing) return;

    const callKey = String(job.service_call_id);
    if (machineRefreshAttemptedForCall.current === callKey) return;
    machineRefreshAttemptedForCall.current = callKey;

    refreshMachines();
  }, [job, machines, refreshMachines]);

  if (jobMissing) return (
    <main className="bc-page--content">
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh' }} // This ensures the container takes at least the full viewport height
      >
        <Grid item>
          Job missing from memory, loading from server...
        </Grid>
      </Grid>
    </main>
  );
  if (loading) {
    return (
      <main className="bc-page--content">
        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: '100vh' }} // This ensures the container takes at least the full viewport height
        >
          <Grid item>
            <CircularProgress color="persiangreen" />
          </Grid>
        </Grid>
      </main>
    );
  }

  if (pageError) return <main className="bc-page--content text-center pt-2"><Container maxWidth="lg" className="job-page">{pageErrorMessage}</Container></main>;

  if (!job) {
    return (
      <main className="bc-page--content">
        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: '100vh' }}
        >
          <Grid item>
            <CircularProgress color="persiangreen" />
          </Grid>
        </Grid>
      </main>
    );
  }

  return (
    <main className="bc-page--content">

      <div className="breadcrumbs container">
        <Container maxWidth="lg">
          <Button component={Link} to="/schedule" startIcon={<KeyboardBackspace />}>Return To Schedule</Button>
        </Container>
      </div>
      <Container maxWidth="lg" className="job-page">
        <JobHeaderMemoized job={job} />
        <Box
          sx={{
            mb: 2,
            width: "100%",
            "& .MuiButtonGroup-root": { width: "100%" },
            "& .MuiButtonGroup-grouped": {
              flex: 1,
              minHeight: 44,
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              textTransform: "none",
              whiteSpace: "nowrap",
              px: { xs: 0.75, sm: 1.5 },
            },
          }}
        >
          <ButtonGroup variant="outlined" fullWidth>
            <InstallMachine job={job} />
            <SwapMachine job={job} defaultMachine={defaultMachine} />
            <RemoveMachine job={job} defaultMachine={defaultMachine} />
          </ButtonGroup>
        </Box>
        <JobDetailsMemoized job={job} />
        <JobActionsBar job={job} />
        <DisplayCompletedJob job={job} />
        <JobAccordionMemoized job={job} />
      </Container>
    </main>
  );
}
